import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config'; 
import { collection, query, where, getDocs, updateDoc, addDoc, doc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuthContext } from '../hooks/useAuthContext'; 
import './CollectionProduct.css'

const AddCollectionForm = () => {
  const { user } = useAuthContext();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [existingCollection, setExistingCollection] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(idTokenResult.claims?.admin || false);
      }
    };
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (name) {
      const checkExistingCollection = async () => {
        const q = query(collection(db, 'collections'), where('name', '==', name));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setExistingCollection(querySnapshot.docs[0]);
        } else {
          setExistingCollection(null);
        }
      };
      checkExistingCollection();
    }
  }, [name]);

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!name || !description || (!image && !existingCollection)) {
      alert('Please fill in all fields');
      setLoading(false);
      return;
    }

    const handleFirestoreUpdate = async (imageUrl = null) => {
      try {
        if (existingCollection) {
          await updateDoc(doc(db, 'collections', existingCollection.id), {
            description,
            imageUrl: imageUrl || existingCollection.data().imageUrl, 
            updatedAt: new Date(),
          });
          alert('Collection updated successfully');
        } else {
          await addDoc(collection(db, 'collections'), {
            name,
            description,
            imageUrl,
            createdAt: new Date(),
          });
          alert('New collection added successfully');
        }
      } catch (error) {
        console.error('Error updating collection:', error);
      }
      setLoading(false);
      setName('');
      setDescription('');
      setImage(null);
      setProgress(0);
    };

    if (image) {
      const storageRef = ref(storage, `collections/${image.name}`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progressPercentage =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progressPercentage); 
        },
        (error) => {
          console.error('Image upload failed:', error);
          setLoading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await handleFirestoreUpdate(downloadURL);
        }
      );
    } else {
      await handleFirestoreUpdate();
    }
  };

  if (!isAdmin) {
    return <div></div>;
  }

  return (
    <div className="form-container">
      <h2>{existingCollection ? 'Edit Collection' : 'Add New Collection'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Collection Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter collection name"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter collection description"
            required
          />
        </div>

        <div className="form-group">
          <label>Collection Image</label>
          <input type="file" onChange={handleImageChange} />
          {progress > 0 && <p>Upload progress: {progress.toFixed(2)}%</p>}
        </div>

        <div className="form-group">
          <button type="submit" disabled={loading}>
            {loading ? 'Saving...' : existingCollection ? 'Update Collection' : 'Add Collection'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCollectionForm;